import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiGet } from "../api/api";

export default function InventarioLista() {
  const [inventario, setInventario] = useState([]);
  const [fotos, setFotos] = useState({});
  const [modalFoto, setModalFoto] = useState(null);

  const [expanded, setExpanded] = useState({});
  const [molduras, setMolduras] = useState({});
  const [expandedMoldura, setExpandedMoldura] = useState({});

  // filtros + paginação
  const [q, setQ] = useState("");
  const [fOperador, setFOperador] = useState("");
  const [fTipoLoc, setFTipoLoc] = useState("");
  const [fDistrito, setFDistrito] = useState("");
  const [fConcelho, setFConcelho] = useState("");
  const [fFreguesia, setFFreguesia] = useState("");

  const [pagina, setPagina] = useState(1);
  const porPagina = 25;

  const navigate = useNavigate();

  // ===========================================================
  // 1️⃣ Carregar inventário e fotos
  // ===========================================================
  useEffect(() => {
    async function load() {
      try {
        const data = await apiGet("/inventario/");
        setInventario(data);

        // carregar fotos de cada item
        for (const item of data) {
          try {
            const fotosItem = await apiGet(`/inventario/${item.id}/fotos`);
            setFotos((prev) => ({ ...prev, [item.id]: fotosItem }));
          } catch (e) {
            console.error("Erro ao carregar fotos do inventário", item.id, e);
          }
        }
      } catch (e) {
        console.error("Erro ao carregar inventário", e);
      }
    }

    load();
  }, []);

  // ===========================================================
  // 2️⃣ Expandir Inventário → Carregar Molduras (1ª vez)
  // ===========================================================
  async function toggleExpand(invId) {
    setExpanded((prev) => ({ ...prev, [invId]: !prev[invId] }));

    // Só carregar molduras a primeira vez
    if (!molduras[invId]) {
      const data = await fetch(`/api/molduras/inventario/${invId}`).then((r) =>
        r.json()
      );
      setMolduras((prev) => ({ ...prev, [invId]: data }));
    }
  }

  // ===========================================================
  // 3️⃣ Expandir Moldura → Mostrar Faces
  // ===========================================================
  function toggleMoldura(molduraId) {
    setExpandedMoldura((prev) => ({
      ...prev,
      [molduraId]: !prev[molduraId],
    }));
  }

  // ===========================================================
  // Modal com Zoom (serve p/ inventário, moldura e face)
  // ===========================================================
  const Modal = ({ foto }) => {
    if (!foto) return null;
    return (
      <div
        onClick={() => setModalFoto(null)}
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          background: "rgba(0,0,0,0.8)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 9999,
          cursor: "zoom-out",
          padding: 10,
        }}
      >
        <img
          src={foto}
          alt="Foto"
          style={{
            maxWidth: "95%",
            maxHeight: "95%",
            borderRadius: 10,
            boxShadow: "0 0 20px rgba(255,255,255,0.5)",
          }}
        />
      </div>
    );
  };

  // ===========================================================
  // Opções de filtros (listas únicas)
  // ===========================================================
  const opOperadores = useMemo(() => {
    const set = new Set();
    inventario.forEach((i) => i.operador?.nome && set.add(i.operador.nome));
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [inventario]);

  const opTipoLoc = useMemo(() => {
    const set = new Set();
    inventario.forEach((i) => i.tipo_localizacao && set.add(i.tipo_localizacao));
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [inventario]);

  const opDistrito = useMemo(() => {
    const set = new Set();
    inventario.forEach((i) => i.distrito && set.add(i.distrito));
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [inventario]);

  const opConcelho = useMemo(() => {
    const set = new Set();
    inventario.forEach((i) => i.concelho && set.add(i.concelho));
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [inventario]);

  const opFreguesia = useMemo(() => {
    const set = new Set();
    inventario.forEach((i) => i.freguesia && set.add(i.freguesia));
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [inventario]);

  // ===========================================================
  // Filtrar + ordenar + paginar
  // ===========================================================
  const inventarioFiltrado = useMemo(() => {
    const qq = q.trim().toLowerCase();

    const base = inventario.filter((item) => {
      if (fOperador && item.operador?.nome !== fOperador) return false;
      if (fTipoLoc && item.tipo_localizacao !== fTipoLoc) return false;
      if (fDistrito && item.distrito !== fDistrito) return false;
      if (fConcelho && item.concelho !== fConcelho) return false;
      if (fFreguesia && item.freguesia !== fFreguesia) return false;

      if (qq) {
        const hay = [
          item.id,
          item.tipo_localizacao,
          item.operador?.nome,
          item.tipologia,
          item.detalhe,
          item.distrito,
          item.concelho,
          item.freguesia,
          item.latitude,
          item.longitude,
        ]
          .filter((x) => x != null)
          .join(" ")
          .toLowerCase();

        if (!hay.includes(qq)) return false;
      }

      return true;
    });

    // últimos primeiro
    return [...base].sort((a, b) => (b.id || 0) - (a.id || 0));
  }, [inventario, q, fOperador, fTipoLoc, fDistrito, fConcelho, fFreguesia]);

  const totalPaginas = Math.max(1, Math.ceil(inventarioFiltrado.length / porPagina));

  // se muda filtro e a pagina fica "fora", ajusta
  useEffect(() => {
    if (pagina > totalPaginas) setPagina(1);
  }, [totalPaginas, pagina]);

  const inventarioPagina = useMemo(() => {
    const inicio = (pagina - 1) * porPagina;
    return inventarioFiltrado.slice(inicio, inicio + porPagina);
  }, [inventarioFiltrado, pagina]);

  function limparFiltros() {
    setQ("");
    setFOperador("");
    setFTipoLoc("");
    setFDistrito("");
    setFConcelho("");
    setFFreguesia("");
    setPagina(1);
  }

  // ===========================================================
  // RENDER
  // ===========================================================
  return (
    <div style={{ padding: "20px" }}>
      <h2>Inventário</h2>

      {/* Filtros */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 10,
          alignItems: "center",
          marginBottom: 15,
          padding: 12,
          background: "#f6f6f6",
          border: "1px solid #ddd",
          borderRadius: 8,
        }}
      >
        <input
          placeholder="Pesquisa (texto livre: Porto, Paranhos, ID, etc.)"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setPagina(1);
          }}
          style={{ padding: 8, minWidth: 260 }}
        />

        <select
          value={fOperador}
          onChange={(e) => {
            setFOperador(e.target.value);
            setPagina(1);
          }}
          style={{ padding: 8 }}
        >
          <option value="">Operador (todos)</option>
          {opOperadores.map((op) => (
            <option key={op} value={op}>
              {op}
            </option>
          ))}
        </select>

        <select
          value={fTipoLoc}
          onChange={(e) => {
            setFTipoLoc(e.target.value);
            setPagina(1);
          }}
          style={{ padding: 8 }}
        >
          <option value="">Tipo Localização (todos)</option>
          {opTipoLoc.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>

        <select
          value={fDistrito}
          onChange={(e) => {
            setFDistrito(e.target.value);
            setPagina(1);
          }}
          style={{ padding: 8 }}
        >
          <option value="">Distrito (todos)</option>
          {opDistrito.map((d) => (
            <option key={d} value={d}>
              {d}
            </option>
          ))}
        </select>

        <select
          value={fConcelho}
          onChange={(e) => {
            setFConcelho(e.target.value);
            setPagina(1);
          }}
          style={{ padding: 8 }}
        >
          <option value="">Concelho (todos)</option>
          {opConcelho.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>

        <select
          value={fFreguesia}
          onChange={(e) => {
            setFFreguesia(e.target.value);
            setPagina(1);
          }}
          style={{ padding: 8 }}
        >
          <option value="">Freguesia (todas)</option>
          {opFreguesia.map((f) => (
            <option key={f} value={f}>
              {f}
            </option>
          ))}
        </select>

        <button
          onClick={limparFiltros}
          style={{
            padding: "8px 12px",
            border: "none",
            borderRadius: 6,
            background: "#666",
            color: "white",
            cursor: "pointer",
          }}
        >
          Limpar
        </button>

        <div style={{ marginLeft: "auto", fontSize: 13, opacity: 0.85 }}>
          A mostrar <strong>{inventarioPagina.length}</strong> de{" "}
          <strong>{inventarioFiltrado.length}</strong> (pág. {pagina}/{totalPaginas})
        </div>
      </div>

      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
        <thead>
          <tr style={{ background: "#ddd" }}>
            <th>ID</th>
            <th>Tipo Localização</th>
            <th>Operador</th>
            <th>Tipologia</th>
            <th>Detalhe</th>
            <th>Distrito</th>
            <th>Concelho</th>
            <th>Freguesia</th>
            <th>GPS</th>
            <th>Fotos</th>
            <th>Ações</th>
          </tr>
        </thead>

        <tbody>
          {inventarioPagina.map((item) => (
            <FragmentRow
              key={item.id}
              item={item}
              fotos={fotos}
              setModalFoto={setModalFoto}
              navigate={navigate}
              expanded={expanded}
              toggleExpand={toggleExpand}
              molduras={molduras}
              expandedMoldura={expandedMoldura}
              toggleMoldura={toggleMoldura}
            />
          ))}
        </tbody>
      </table>

      {/* Paginação */}
      <div style={{ marginTop: 15, display: "flex", gap: 6, flexWrap: "wrap" }}>
        <button
          onClick={() => setPagina((p) => Math.max(1, p - 1))}
          disabled={pagina === 1}
          style={btnPag(pagina === 1)}
        >
          ◀
        </button>

        {Array.from({ length: totalPaginas }, (_, i) => i + 1).map((p) => (
          <button
            key={p}
            onClick={() => setPagina(p)}
            style={{
              ...btnPag(false),
              background: p === pagina ? "#1976d2" : "#e0e0e0",
              color: p === pagina ? "white" : "black",
            }}
          >
            {p}
          </button>
        ))}

        <button
          onClick={() => setPagina((p) => Math.min(totalPaginas, p + 1))}
          disabled={pagina === totalPaginas}
          style={btnPag(pagina === totalPaginas)}
        >
          ▶
        </button>
      </div>

      <Modal foto={modalFoto} />
    </div>
  );
}

// =====================================
// Helpers / Component interno
// =====================================

function btnPag(disabled) {
  return {
    padding: "6px 10px",
    borderRadius: 6,
    border: "none",
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.5 : 1,
    background: "#e0e0e0",
  };
}

function FragmentRow({
  item,
  fotos,
  setModalFoto,
  navigate,
  expanded,
  toggleExpand,
  molduras,
  expandedMoldura,
  toggleMoldura,
}) {
  return (
    <>
      {/* Linha principal */}
      <tr style={{ borderBottom: "1px solid #ccc" }}>
        <td>{item.id}</td>
        <td>{item.tipo_localizacao}</td>
        <td>{item.operador?.nome || "—"}</td>
        <td>{item.tipologia}</td>
        <td>{item.detalhe}</td>
        <td>{item.distrito}</td>
        <td>{item.concelho}</td>
        <td>{item.freguesia}</td>

        <td>
          {item.latitude != null && item.longitude != null
            ? `${item.latitude}, ${item.longitude}`
            : "—"}
        </td>

        <td>
          {fotos[item.id] && fotos[item.id].length > 0 ? (
            fotos[item.id].map((f) => (
              <img
                key={f.id}
                src={f.foto_path}
                alt="Foto"
                onClick={() => setModalFoto(f.foto_path)}
                style={{
                  width: 60,
                  height: 60,
                  objectFit: "cover",
                  borderRadius: 8,
                  marginRight: 4,
                  cursor: "zoom-in",
                  border: "1px solid #ddd",
                }}
              />
            ))
          ) : (
            "—"
          )}
        </td>

        <td>
          <button
            onClick={() => navigate(`/inventario/${item.id}`)}
            style={{
              padding: "6px 12px",
              background: "#1976d2",
              color: "white",
              border: "none",
              borderRadius: 6,
              cursor: "pointer",
              marginRight: 5,
            }}
          >
            Ver Detalhes
          </button>

          <button
            onClick={() => toggleExpand(item.id)}
            style={{
              padding: "6px 12px",
              background: "#555",
              color: "white",
              border: "none",
              borderRadius: 6,
              cursor: "pointer",
            }}
          >
            {expanded[item.id] ? "Fechar" : "Expandir"}
          </button>
        </td>
      </tr>

      {/* Expansão de MOLDURAS */}
      {expanded[item.id] && (
        <tr>
          <td colSpan="12" style={{ background: "#f6f6f6", padding: 15 }}>
            <h4 style={{ marginTop: 0 }}>Molduras</h4>

            {(!molduras[item.id] || molduras[item.id].length === 0) && (
              <p>Sem molduras registadas.</p>
            )}

            {molduras[item.id]?.map((m) => (
              <div
                key={m.id}
                style={{
                  border: "1px solid #ccc",
                  padding: 12,
                  marginBottom: 10,
                  borderRadius: 6,
                  background: "white",
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <strong>Moldura #{m.id}</strong>

                  <button
                    onClick={() => toggleMoldura(m.id)}
                    style={{
                      padding: "4px 8px",
                      background: "#333",
                      color: "white",
                      border: "none",
                      borderRadius: 4,
                      cursor: "pointer",
                    }}
                  >
                    {expandedMoldura[m.id] ? "Ocultar Faces" : "Mostrar Faces"}
                  </button>
                </div>

                {m.foto_path && (
                  <img
                    src={m.foto_path}
                    alt="foto moldura"
                    onClick={() => setModalFoto(m.foto_path)}
                    style={{
                      width: 120,
                      height: 120,
                      objectFit: "cover",
                      borderRadius: 8,
                      marginTop: 10,
                      cursor: "zoom-in",
                      border: "1px solid #ddd",
                    }}
                  />
                )}

                {/* Expansão de Faces */}
                {expandedMoldura[m.id] && (
                  <div style={{ marginTop: 10, paddingLeft: 20 }}>
                    <h5 style={{ margin: "8px 0" }}>Faces</h5>

                    {(!m.faces || m.faces.length === 0) && <p>Sem faces.</p>}

                    {m.faces?.map((f) => (
                      <div key={f.id} style={{ marginBottom: 10 }}>
                        <strong>{f.lado}</strong> — {f.identificador}

                        {f.foto_path && (
                          <img
                            src={f.foto_path}
                            alt="foto face"
                            onClick={() => setModalFoto(f.foto_path)}
                            style={{
                              width: 80,
                              height: 80,
                              objectFit: "cover",
                              marginLeft: 10,
                              borderRadius: 6,
                              cursor: "zoom-in",
                              border: "1px solid #ddd",
                            }}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </td>
        </tr>
      )}
    </>
  );
}
